import { memo, forwardRef, type Ref, type ReactNode } from 'react';
import { Skeleton, Typography, type SkeletonProps } from '@mui/material';
import { type SxProps, type Theme, useTheme } from '@mui/material/styles';

interface WidgetSkeletonProps extends SkeletonProps {
  text?: string;
  sx?: SxProps<Theme>;
  children?: ReactNode;
}

const WidgetSkeleton = memo(
  forwardRef(function WidgetSkeleton(
    {
      text = 'Loading...',
      sx,
      animation = 'pulse',
      children,
      ...props
    }: WidgetSkeletonProps,
    ref: Ref<HTMLDivElement>,
  ) {
    const theme = useTheme();

    return (
      <Skeleton
        className="widget-skeleton-box-loader"
        data-testid="widget-skeleton-box-loader"
        component="div"
        variant="rectangular"
        animation={animation}
        width={props?.width || '100%'}
        height={props?.height || 50}
        sx={{
          borderRadius: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          ...(typeof sx === 'function' ? sx(theme) : sx),
        }}
        {...props}
        ref={ref}
      >
        {text && !children && (
          <Typography
            className="widget-skeleton-box-text"
            data-testid="widget-skeleton-box-text"
            variant="caption"
            sx={{
              p: 1,
              color:
                theme.palette.mode === 'dark'
                  ? theme.blox.colors.lightgray
                  : theme.blox.colors.offblack,
              visibility: 'visible !important',
              alignSelf: 'center',
              alignItems: 'center',
            }}
          >
            {text}
          </Typography>
        )}

        {children}
      </Skeleton>
    );
  }),
);

export default WidgetSkeleton;
